import React, { useState } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";

// Tenants are not a booking-side concept: they are the units of an asset
// register. So this writes to the customers API — the same rows Site → Asset
// Register reads — rather than keeping a second copy the two lists would have
// to be kept in step. Adding or editing here shows up there, and vice versa.
export function TenantModal({ siteId, areaId, row, onClose, onSaved }) {
  const isEdit = !!row;
  const [form, setForm] = useState({
    unit_number: row?.unit_number || "",
    name: row?.unit_name || "",
    floor_level: row?.floor_level || "",
    contact_name: row?.tenant_name || "",
    mobile: row?.tenant_mobile || "",
    email: row?.tenant_email || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.unit_number.trim() && !form.name.trim())
      return setError("Give the tenancy a unit number or a name.");

    setSaving(true);
    setError("");
    const unitFields = {
      unit_number: form.unit_number,
      name: form.name,
      floor_level: form.floor_level,
    };
    const hasContact =
      form.contact_name.trim() || form.mobile.trim() || form.email.trim();

    try {
      let unitId = row?.unit_id;
      if (isEdit) {
        await axios.patch(`${API}/site-units/${unitId}/`, unitFields);
      } else {
        const created = await axios.post(`${API}/site-units/`, {
          ...unitFields,
          unit_type: "tenant",
          site: siteId,
          area: areaId,
        });
        unitId = created.data.id;
      }

      if (hasContact) {
        const contact = {
          name: form.contact_name,
          mobile: form.mobile,
          email: form.email,
        };
        if (row?.tenant_contact_id) {
          await axios.patch(
            `${API}/unit-contacts/${row.tenant_contact_id}/`,
            contact
          );
        } else {
          await axios.post(`${API}/unit-contacts/`, {
            ...contact,
            role: "primary",
            unit: unitId,
          });
        }
      }
      onSaved();
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          `Could not ${isEdit ? "save" : "add"} that tenant.`
      );
      setSaving(false);
    }
  };

  return (
    <SimpleModal
      title={isEdit ? "Edit tenant" : "Add tenant"}
      onClose={onClose}
      onSave={handleSave}
      saving={saving}
      error={error}
      saveLabel={isEdit ? "Save" : "Add"}
      wide
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "12px" }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Unit number</label>
          <input
            style={inputStyle}
            value={form.unit_number}
            onChange={(e) => set("unit_number", e.target.value)}
            placeholder="12"
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Tenancy name</label>
          <input
            style={inputStyle}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Bobs Burgers"
          />
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Floor / level</label>
        <input
          style={inputStyle}
          value={form.floor_level}
          onChange={(e) => set("floor_level", e.target.value)}
          placeholder="Ground Floor"
        />
      </div>

      <div
        style={{
          borderTop: `1px solid ${colors.border}`,
          margin: "4px 0 16px",
          paddingTop: "16px",
          fontSize: "12px",
          fontWeight: "700",
          color: colors.slate,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        Primary contact
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Name</label>
        <input
          style={inputStyle}
          value={form.contact_name}
          onChange={(e) => set("contact_name", e.target.value)}
          placeholder="Who to call about access"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Mobile</label>
          <input
            style={inputStyle}
            value={form.mobile}
            onChange={(e) => set("mobile", e.target.value)}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Email</label>
          <input
            style={inputStyle}
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </div>
      </div>

      <div style={{ fontSize: "12px", color: colors.slate }}>
        Saved against this site's asset register, so it shows in both places.
      </div>
    </SimpleModal>
  );
}
